import { createContext, useContext, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

export type ToastTone = "success" | "neutral" | "progress" | "error";

export type ToastOptions = {
  message: ReactNode;
  /**
   * success (green, default): a change was saved · neutral (dark): a quick
   * result that changes no data — copied, cleared · progress (dark, spinner):
   * a long action still running; stays until replaced · error: avoid — a
   * failed action shows a Banner where it happened, never a toast.
   */
  tone?: ToastTone;
  icon?: LucideIcon | false;
  /** "Undo", "View", "Download": the one follow-up. */
  actionLabel?: string;
  onAction?: () => void;
  /** ms; default 2000, 5000 with an action; progress stays until replaced. */
  duration?: number;
};

export type ToastApi = {
  /** Shows one floating toast (replacing the current one) and returns its id. */
  show: (options: ToastOptions) => number;
  /** Replaces the toast with this id if it is still showing (progress → result). */
  update: (id: number, options: ToastOptions) => void;
  dismiss: (id?: number) => void;
};

export const ToastContext = createContext<ToastApi | null>(null);

/** The shell's floating toast dock (bottom centre). */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast needs <ToastProvider> (AppShell renders it)");
  return api;
}
